import { normalize } from './dates';
import { NO_ROLE_RULES } from './roleRules';
import type { MinistryDraft } from './transitions';
import { validateMinistry } from './transitions';
import type { Ministry, MinistryIconKey, Role, RoleIconKey, RoleSex } from './types';

/**
 * Modelos de ministério ("Usar modelo" em Ministérios): um ponto de partida com as
 * funções mais comuns de cada área, para o administrador marcar as que quer, ajustar
 * nome, ícone, quantidade e sexo permitido e criar o ministério de uma vez. Juntam os
 * ministérios dos dados de exemplo e os das referências do projeto de design.
 */

export interface TemplateRole {
  name: string;
  icon: RoleIconKey;
  qty: number;
  allowedSex: RoleSex;
  /** Já vem marcada no painel; as demais são opcionais. */
  selected: boolean;
}

export interface MinistryTemplate {
  id: string;
  name: string;
  description: string;
  color: string;
  icon: MinistryIconKey;
  roles: readonly TemplateRole[];
}

const role = (name: string, icon: RoleIconKey, selected: boolean, qty = 1, allowedSex: RoleSex = 'any') => ({
  name,
  icon,
  qty,
  allowedSex,
  selected,
});

export const MINISTRY_TEMPLATES: readonly MinistryTemplate[] = [
  {
    id: 'louvor',
    name: 'Louvor',
    description: 'Ministros, vocais, banda e mesa de som.',
    color: '#16A34A',
    icon: 'music',
    roles: [
      role('Ministro', 'leader', true),
      role('Vocal', 'mic', true, 2),
      role('Backing vocal', 'choir', false, 2),
      role('Violão', 'guitar', true),
      role('Guitarra', 'guitar', false),
      role('Baixo', 'bass', true),
      role('Teclado', 'keyboard', true),
      role('Piano', 'piano', false),
      role('Bateria', 'drums', true),
      role('Percussão', 'percussion', false),
      role('Mesa de som', 'mixer', false),
    ],
  },
  {
    id: 'multimidia',
    name: 'Multimídia',
    description: 'Projeção, som, iluminação, transmissão e registro do culto.',
    color: '#0891B2',
    icon: 'monitor',
    roles: [
      role('Projeção', 'projector', true),
      role('Som', 'audio', true),
      role('Transmissão', 'live', true),
      role('Iluminação', 'lights', false),
      role('Fotógrafo', 'camera', false),
      role('Videomaker', 'video', false),
      role('Social media', 'social', false),
      role('Story', 'story', false),
      role('Editor de vídeo', 'videoEditor', false),
      role('Editor de foto', 'photoEditor', false),
    ],
  },
  {
    id: 'transmissao',
    name: 'Transmissão / Live',
    description: 'Transmissão ao vivo e captação do culto.',
    color: '#DC2626',
    icon: 'radio',
    roles: [
      role('Transmissão', 'live', true),
      role('Câmera', 'camera', true),
      role('Técnico live', 'headset', true),
      role('Diretor de vídeo', 'director', true),
      role('Operador de câmera', 'cameraOperator', false),
      role('Videomaker', 'video', false),
      role('Operador de gimbal', 'gimbal', false),
      role('Coordenação de mídia', 'mediaDesk', false),
    ],
  },
  {
    id: 'midia',
    name: 'Mídia / Comunicação',
    description: 'Conteúdo e redes sociais da igreja.',
    color: '#C026D3',
    icon: 'smartphone',
    roles: [
      role('Social media', 'social', true),
      role('Story', 'story', true),
      role('Designer', 'designer', true),
      role('Editor de vídeo', 'videoEditor', true),
      role('Reels', 'reels', false),
      role('Criador de artes', 'art', false),
      role('Editor de foto', 'photoEditor', false),
      role('Copywriter', 'copy', false),
      role('Planejamento de conteúdo', 'planning', false),
      role('Conteúdo para redes sociais', 'content', false),
    ],
  },
  {
    id: 'liturgia',
    name: 'Liturgia / Culto',
    description: 'Condução do culto, ordem de participação e momentos litúrgicos.',
    color: '#92400E',
    icon: 'church',
    roles: [
      role('Abertura do culto', 'opening', true),
      role('Boas-vindas', 'welcome', true),
      role('Palavra', 'word', true),
      role('Oferta', 'offering', true),
      role('Anúncios', 'announcements', true),
      role('Encerramento', 'closing', true),
      role('Preleção', 'preaching', false),
      role('Monitor de culto', 'monitor', false),
    ],
  },
  {
    id: 'recepcao',
    name: 'Recepção / Apoio',
    description: 'Recepção, acolhimento, organização e apoio geral do culto.',
    color: '#2563EB',
    icon: 'door',
    roles: [
      role('Portaria', 'door', true, 2),
      role('Acolhimento', 'users', true, 2),
      role('Boas-vindas', 'welcome', true),
      role('Coordenador', 'coordinator', false),
      role('Guardiões', 'guard', false, 2),
      role('Suporte geral', 'support', false),
      role('Limpeza', 'cleaning', false),
      role('Organização', 'organization', false),
      role('Monitor de culto', 'monitor', false),
    ],
  },
  {
    id: 'infantil',
    name: 'Infantil / Culto infantil',
    description: 'Aulas, dinâmicas e berçário durante os cultos e no culto infantil.',
    color: '#DB2777',
    icon: 'smile',
    roles: [
      role('Coordenador', 'coordinator', true),
      role('Professor(a)', 'teacher', true, 2),
      role('Dinâmica', 'dynamics', true, 2),
      role('Berçário', 'nursery', true, 1, 'F'),
      role('Auxiliar', 'support', false),
      role('Oração inicial', 'prayer', false),
      role('Palavra', 'word', false),
      role('Boas-vindas', 'welcome', false),
      role('Organização', 'organization', false),
    ],
  },
];

/** Função do painel do modelo, já com o que o administrador mudou. */
export interface TemplateRoleDraft {
  /** Chave local da linha (as linhas podem ter nome repetido enquanto se edita). */
  key: string;
  name: string;
  icon: RoleIconKey;
  qty: number;
  allowedSex: RoleSex;
  selected: boolean;
}

export interface TemplateDraft {
  templateId: string;
  ministry: MinistryDraft;
  roles: TemplateRoleDraft[];
}

/** Máximo de funções criadas de uma vez (o mesmo do contrato da API). */
export const MAX_TEMPLATE_ROLES = 30;

export function templateDraft(template: MinistryTemplate): TemplateDraft {
  return {
    templateId: template.id,
    ministry: {
      id: null,
      name: template.name,
      description: template.description,
      color: template.color,
      icon: template.icon,
      leaderIds: [],
      // O modelo de Recepção já nasce como o ministério de acolhimento (os visitantes).
      welcome: template.id === 'recepcao',
    },
    roles: template.roles.map((r, index) => ({ key: `${template.id}-${index}`, ...r })),
  };
}

/** As funções marcadas, na ordem do painel. */
export function selectedRoles(draft: TemplateDraft): TemplateRoleDraft[] {
  return draft.roles.filter((r) => r.selected);
}

/** Erros por linha do painel: nome vazio ou repetido (sem acento e sem caixa) entre as marcadas. */
export function templateRoleErrors(draft: TemplateDraft): Record<string, string> {
  const errors: Record<string, string> = {};
  const seen = new Set<string>();

  for (const r of selectedRoles(draft)) {
    const name = normalize(r.name.trim());

    if (!name) errors[r.key] = 'Dê um nome à função.';
    else if (seen.has(name)) errors[r.key] = 'Já existe uma função marcada com esse nome.';

    seen.add(name);
  }

  return errors;
}

/** O que impede criar o ministério a partir do modelo, ou `null`. */
export function templateDraftError(ministries: readonly Ministry[], draft: TemplateDraft): string | null {
  const ministryError = validateMinistry([...ministries], draft.ministry);

  if (ministryError) return ministryError;

  const roles = selectedRoles(draft);

  if (!roles.length) return 'Marque ao menos uma função.';
  if (roles.length > MAX_TEMPLATE_ROLES) return `Marque no máximo ${MAX_TEMPLATE_ROLES} funções.`;
  if (Object.keys(templateRoleErrors(draft)).length) return 'Confira os nomes das funções marcadas.';

  return null;
}

/** A função que entra no ministério novo: os campos do modelo, sem regras avançadas. */
export interface RoleSeed {
  name: string;
  icon: RoleIconKey;
  qty: number;
  allowedSex: RoleSex;
}

export function roleSeeds(draft: TemplateDraft): RoleSeed[] {
  return selectedRoles(draft).map(({ name, icon, qty, allowedSex }) => ({
    name: name.trim(),
    icon,
    qty,
    allowedSex,
  }));
}

export function roleFromSeed(seed: RoleSeed, id: string): Role {
  return {
    id,
    name: seed.name.trim(),
    qty: seed.qty,
    allowedSex: seed.allowedSex,
    rules: [],
    leaderIds: [],
    icon: seed.icon,
    ...structuredClone(NO_ROLE_RULES),
  };
}
